import React, { useState } from "react";
import {
  Form,
  Input,
  Button,
  Typography,
  Image,
  Grid,
} from "antd";
import {
  MailOutlined,
  LockOutlined,
  ArrowLeftOutlined,
  KeyOutlined,
  AppstoreOutlined,
  TeamOutlined,
  DollarCircleOutlined,
} from "@ant-design/icons";
import "./login.css";

const LoginForm = ({
  onSubmit,
  onForgotSubmit,
  loginLoading,
  forgotLoading,
  logo,
  appName,
}) => {
  const [isForgot, setIsForgot] = useState(false);
  const [form] = Form.useForm();
  const screens = Grid.useBreakpoint();
  const isMobile = !screens.md;

  const backToLogin = () => {
    setIsForgot(false);
    form.resetFields();
  };

  const onFinish = (values) => {
    if (isForgot) {
      onForgotSubmit?.(values, form, backToLogin);
    } else {
      onSubmit(values);
    }
  };

  const LogoChip = ({ className, width = 140, height = 46 }) => (
    <div className={`login-logo-chip ${className || ""}`}>
      <Image
        width={width}
        height={height}
        preview={false}
        src={logo}
        className="logo-img"
        alt="Company Logo"
      />
    </div>
  );

  return (
    <div className="login-page">
      {/* Desktop: a full side panel. Mobile: becomes a compact gradient
          header banner instead of disappearing -- see login.css. Reusing
          this one panel (glows included) means mobile gets the same bold
          brand treatment as desktop instead of a separately-built one. */}
      <div className="login-brand-panel">
        <span className="login-glow login-glow-a" />
        <span className="login-glow login-glow-b" />
        <span className="login-glow login-glow-c" />

        <LogoChip width={isMobile ? 180 : 140} height={isMobile ? 59 : 46} />

        <div className="login-brand-copy">
          <Typography.Title level={2} className="login-brand-headline">
            Welcome to {appName || "your workspace"}
          </Typography.Title>
          <Typography.Text className="login-brand-tagline">
            One platform to run your entire business.
          </Typography.Text>

          {/* Hidden on mobile (login.css) -- keeps the banner short enough
              that the form stays visible without scrolling. */}
          <ul className="login-brand-features">
            <li>
              <span className="login-feature-icon"><AppstoreOutlined /></span>
              Sales, purchase &amp; inventory
            </li>
            <li>
              <span className="login-feature-icon"><TeamOutlined /></span>
              CRM, leads &amp; deals
            </li>
            <li>
              <span className="login-feature-icon"><DollarCircleOutlined /></span>
              HR, attendance &amp; payroll
            </li>
          </ul>
        </div>
      </div>

      {/* Form panel */}
      <div className="login-form-panel">
        <div className="login-form-inner">

          {isForgot ? (
            <div className="login-header">
              <div className="login-icon-badge">
                <KeyOutlined />
              </div>
              <Typography.Title level={3} className="login-title">
                Reset your password
              </Typography.Title>
              <Typography.Text className="login-subtitle">
                Enter the email on your account and we'll send you a link to
                reset your password.
              </Typography.Text>
            </div>
          ) : (
            <div className="login-header">
              <Typography.Title level={3} className="login-title">
                Sign in
              </Typography.Title>
              <Typography.Text className="login-subtitle">
                Enter your details to access your account.
              </Typography.Text>
            </div>
          )}

          <Form
            form={form}
            data-testid="login-form"
            name="login"
            layout="vertical"
            onFinish={onFinish}
            autoComplete="on"
          >
            <Form.Item
              label="Email"
              name="email"
              rules={[
                { required: true, message: "Please input your email!" },
                { type: "email", message: "Enter a valid email!" },
              ]}
            >
              <Input
                size="large"
                prefix={<MailOutlined className="login-input-icon" />}
                placeholder="Enter your email"
              />
            </Form.Item>

            {!isForgot && (
              <Form.Item
                label="Password"
                name="password"
                rules={[
                  { required: true, message: "Please input your password!" },
                  { min: 6, message: "Password must be at least 6 characters." },
                ]}
              >
                <Input.Password
                  size="large"
                  prefix={<LockOutlined className="login-input-icon" />}
                  placeholder="Enter your password"
                />
              </Form.Item>
            )}

            {!isForgot && (
              <div className="login-forgot-row">
                <Typography.Link
                  className="toggle-link"
                  onClick={() => setIsForgot(true)}
                >
                  Forgot password?
                </Typography.Link>
              </div>
            )}

            <Form.Item style={{ marginBottom: isForgot ? 12 : 0 }}>
              <Button
                type="primary"
                htmlType="submit"
                size="large"
                block
                className="login-btn"
                loading={isForgot ? forgotLoading : loginLoading}
              >
                {isForgot ? "Send reset link" : "Sign In"}
              </Button>
            </Form.Item>
          </Form>

          {isForgot && (
            <div className="login-forgot-row login-forgot-row-center">
              <Typography.Link className="toggle-link back-link" onClick={backToLogin}>
                <ArrowLeftOutlined /> Back to sign in
              </Typography.Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default LoginForm;
